import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/apiService";
import { API_ENDPOINTS } from "../config/api";
import { handleError } from "../utils/errorHandler";
import "./NotificationPanel.css";

const formatRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const typeIcon = (type, category) => {
  if (category?.includes("reminder")) return "⏰";
  if (type === "Todo") return "✅";
  if (type === "Lead") return "📋";
  if (type === "Opportunity") return "🎯";
  if (type === "Client") return "👤";
  if (type === "Property") return "🏢";
  return "🔔";
};

const NotificationPanel = ({ isOpen, onClose, onCountChange }) => {
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const [tab, setTab] = useState("unread");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const loadNotifications = useCallback(
    async (pageNum = 1, replace = true) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(pageNum),
          limit: "25",
        });
        if (tab === "unread") params.set("unread", "true");

        const res = await apiService.get(
          `${API_ENDPOINTS.notifications.base}?${params.toString()}`
        );
        const batch = res.data || [];
        setItems((prev) => (replace ? batch : [...prev, ...batch]));
        setPage(pageNum);
        setHasMore(pageNum < (res.totalPages || 1));
      } catch (error) {
        handleError(error, { context: "NotificationPanel - load", showToast: false });
      } finally {
        setLoading(false);
      }
    },
    [tab]
  );

  const refreshCount = useCallback(async () => {
    try {
      const res = await apiService.get(API_ENDPOINTS.notifications.count);
      onCountChange?.(res.count ?? 0);
    } catch {
      /* silent */
    }
  }, [onCountChange]);

  useEffect(() => {
    if (!isOpen) return;
    loadNotifications(1, true);
  }, [isOpen, tab, loadNotifications]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const markAsRead = async (id) => {
    try {
      await apiService.put(API_ENDPOINTS.notifications.markRead(id), null);
      setItems((prev) =>
        tab === "unread" ? prev.filter((n) => n._id !== id) : prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
      await refreshCount();
    } catch (error) {
      handleError(error, { context: "NotificationPanel - markRead" });
    }
  };

  const markAllRead = async () => {
    try {
      await apiService.put(API_ENDPOINTS.notifications.markAllRead, null);
      if (tab === "unread") setItems([]);
      else setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
      onCountChange?.(0);
    } catch (error) {
      handleError(error, { context: "NotificationPanel - markAllRead" });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="notif-panel" ref={panelRef} role="dialog" aria-label="Notifications">
      <header className="notif-panel__header">
        <h3>Notifications</h3>
        <div className="notif-panel__tabs">
          <button
            type="button"
            className={tab === "unread" ? "active" : ""}
            onClick={() => setTab("unread")}
          >
            Unread
          </button>
          <button
            type="button"
            className={tab === "all" ? "active" : ""}
            onClick={() => setTab("all")}
          >
            All
          </button>
        </div>
      </header>

      {items.length > 0 && (
        <div className="notif-panel__actions">
          <button type="button" className="notif-panel__mark-all" onClick={markAllRead}>
            Mark all as read
          </button>
        </div>
      )}

      <div className="notif-panel__list">
        {loading && items.length === 0 ? (
          <p className="notif-panel__empty">Loading…</p>
        ) : items.length === 0 ? (
          <div className="notif-panel__empty-state">
            <span aria-hidden>✓</span>
            <p>{tab === "unread" ? "You're all caught up" : "No notifications yet"}</p>
          </div>
        ) : (
          items.map((n) => (
            <article
              key={n._id}
              className={`notif-panel__item${n.isRead ? " is-read" : ""}`}
              onClick={() => {
                if (!n.isRead) markAsRead(n._id);
                if (n.type === "Todo" && n.entityId) {
                  onClose?.();
                  navigate(`/todos?id=${n.entityId}`);
                }
              }}
              style={n.type === "Todo" ? { cursor: "pointer" } : undefined}
            >
              <span className="notif-panel__icon" aria-hidden>
                {typeIcon(n.type, n.category)}
              </span>
              <div className="notif-panel__body">
                <p className="notif-panel__message">{n.message}</p>
                <div className="notif-panel__meta">
                  <span>{n.type}{n.action ? ` · ${n.action}` : ""}</span>
                  <span>{formatRelativeTime(n.createdAt)}</span>
                </div>
              </div>
              {!n.isRead && (
                <button
                  type="button"
                  className="notif-panel__read-btn"
                  onClick={() => markAsRead(n._id)}
                  title="Mark as read"
                  aria-label="Mark as read"
                >
                  ●
                </button>
              )}
            </article>
          ))
        )}
      </div>

      {hasMore && (
        <footer className="notif-panel__footer">
          <button
            type="button"
            disabled={loading}
            onClick={() => loadNotifications(page + 1, false)}
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        </footer>
      )}
    </div>
  );
};

export default NotificationPanel;
